import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  Grid,
  CircularProgress,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../services/api";

const Users = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalUsers: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [statusDialog, setStatusDialog] = useState({ open: false, user: null });
  const [downloadingAll, setDownloadingAll] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: pagination.currentPage,
        limit: 10,
        sort: sortBy,
        order: sortOrder,
      });

      if (searchTerm) {
        params.append("search", searchTerm);
      }

      const response = await api.get(`/users?${params}`);
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, searchTerm, sortBy, sortOrder]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await api.delete(`/users/${userId}`);
        toast.success("User deleted successfully");
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to delete user");
      }
    }
  };

  const handleStatusChange = async (userId, newStatus) => {
    try {
      await api.patch(`/users/${userId}/status`, { status: newStatus });
      toast.success(
        `User ${
          newStatus === "active" ? "activated" : "deactivated"
        } successfully`
      );
      setStatusDialog({ open: false, user: null });
      fetchUsers();
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to update user status"
      );
    }
  };

  const handleCreateUser = () => {
    navigate("/users/add");
  };

  const handleEditUser = (user) => {
    navigate("/users/edit", { state: { user } });
  };

  const openStatusDialog = (user) => {
    setStatusDialog({ open: true, user });
  };

  const handleDownloadAllUsers = async () => {
    try {
      setDownloadingAll(true);
      const response = await api.get("/users/export/all");
      const exportUsers = Array.isArray(response.data?.users)
        ? response.data.users
        : [];

      const headers = [
        "First Name",
        "Last Name",
        "Email",
        "Role",
        "Status",
        "Address",
        "City",
        "State",
        "Zip Code",
        "Country",
        "Phone",
      ];

      const formatCsvValue = (value) => {
        if (value === null || value === undefined || value === "") {
          return '""';
        }
        const stringValue = String(value);
        const escapedValue = stringValue.replace(/"/g, '""');
        return `"${escapedValue}"`;
      };

      const rows = exportUsers.map((user) => {
        const address = user?.defaultAddress || {};
        const normalizedPhone = user?.phone || address.phone || "";

        return [
          user?.firstName || "",
          user?.lastName || "",
          user?.email || "",
          user?.legacyRole || "",
          user?.status || "",
          address.address || "",
          address.city || "",
          address.state || "",
          address.zipCode || "",
          address.country || "",
          normalizedPhone,
        ];
      });

      const csvRows = [headers, ...rows]
        .map((line) => line.map(formatCsvValue).join(","))
        .join("\n");
      const csvContent = "\ufeff" + csvRows;

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "users_export.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Users exported successfully");
    } catch (error) {
      console.error("Failed to export users:", error);
      toast.error(error.response?.data?.message || "Failed to export users");
    } finally {
      setDownloadingAll(false);
    }
  };

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">User Management</Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={
              downloadingAll ? <CircularProgress size={18} /> : <DownloadIcon />
            }
            onClick={handleDownloadAllUsers}
            disabled={downloadingAll}
          >
            {downloadingAll ? "Exporting..." : "Download Users"}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateUser}
          >
            Add User
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} mb={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Search users"
            value={searchTerm}
            onChange={handleSearch}
            placeholder="Search by name or email..."
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Sort By</InputLabel>
            <Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="createdAt">Created Date</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Order</InputLabel>
            <Select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
            >
              <MenuItem value="asc">Ascending</MenuItem>
              <MenuItem value="desc">Descending</MenuItem>
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Button onClick={() => handleSort("name")}>
                      Name{" "}
                      {sortBy === "name" && (sortOrder === "asc" ? "↑" : "↓")}
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Button onClick={() => handleSort("email")}>
                      Email{" "}
                      {sortBy === "email" && (sortOrder === "asc" ? "↑" : "↓")}
                    </Button>
                  </TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>
                    <Button onClick={() => handleSort("createdAt")}>
                      Created{" "}
                      {sortBy === "createdAt" &&
                        (sortOrder === "asc" ? "↑" : "↓")}
                    </Button>
                  </TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map((user) => (
                  <TableRow key={user._id}>
                    <TableCell>
                      {user.firstName} {user.lastName}
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.legacyRole}
                        color={
                          user.legacyRole === "admin" ? "primary" : "default"
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.status}
                        color={user.status === "active" ? "success" : "error"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(user.createdAt).toLocaleDateString("en-IN", {
                        timeZone: "Asia/Kolkata",
                      })}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => handleEditUser(user)}
                        color="primary"
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => openStatusDialog(user)}
                        disabled={user.legacyRole === "admin"}
                        color={user.status === "active" ? "warning" : "success"}
                        size="small"
                      >
                        {user.status === "active" ? (
                          <BlockIcon />
                        ) : (
                          <CheckCircleIcon />
                        )}
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteUser(user._id)}
                        disabled={user.legacyRole === "admin"}
                        color="error"
                        size="small"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={pagination.totalPages}
              page={pagination.currentPage}
              onChange={(event, page) =>
                setPagination((prev) => ({ ...prev, currentPage: page }))
              }
              color="primary"
            />
          </Box>
        </>
      )}

      {/* Status Change Dialog */}
      <Dialog
        open={statusDialog.open}
        onClose={() => setStatusDialog({ open: false, user: null })}
      >
        <DialogTitle>Change User Status</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to{" "}
            {statusDialog.user?.status === "active" ? "deactivate" : "activate"}{" "}
            {statusDialog.user?.firstName} {statusDialog.user?.lastName}?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialog({ open: false, user: null })}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              handleStatusChange(
                statusDialog.user._id,
                statusDialog.user.status === "active" ? "inactive" : "active"
              )
            }
            variant="contained"
            color={
              statusDialog.user?.status === "active" ? "warning" : "success"
            }
          >
            {statusDialog.user?.status === "active" ? "Deactivate" : "Activate"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Users;
